import { FottServiceType, PiggingProductType } from "@services/terminal-services/types";
import { toMinute } from "@utils/date-time";
import type { TFunction } from "i18next";
import { z } from "zod";
import {
  FOTT_SERVICE_REQUEST_FIELDS as F,
  MAX_BYPASS_ATTACHMENTS,
  MAX_BYPASS_SHORT_TEXT_LENGTH,
  MAX_BYPASS_TEXT_LENGTH,
  MAX_FOTT_SERVICE_REMARKS_LENGTH,
  MAX_GANTRY_CRANE_TEXT_LENGTH,
  MAX_NITROGEN_ATTACHMENTS,
  MAX_NITROGEN_SHORT_TEXT_LENGTH,
  MAX_NITROGEN_TEXT_LENGTH,
  MAX_NITROGEN_USAGE_HOURS_DIGITS,
  MAX_OPERATOR_LICENSE_FILES,
  MAX_PIGGING_ATTACHMENTS,
  MAX_PIGGING_SHORT_TEXT_LENGTH,
  MAX_PIGGING_TANK_NO_LENGTH,
  MAX_PIGGING_TEXT_LENGTH,
  MAX_VESSEL_IMO_LENGTH,
  OTHER_COMPANY_VALUE,
  TERMINAL_SERVICE_ATTACHMENT_DOCUMENT_TYPES,
} from "./constants";

type RefinementContext = z.RefinementCtx;

const dateLikeSchema = z.union([z.date(), z.string(), z.null()]).optional();

const addRequiredIssue = (ctx: RefinementContext, path: string, label: string, t: TFunction) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [path],
    message: t("form-validation.mandatory", { label }),
  });
};

const addIssue = (ctx: RefinementContext, path: string, message: string) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [path],
    message,
  });
};

const isBlank = (value: unknown) => typeof value !== "string" || value.trim().length === 0;

const isUnselectedBoolean = (value: unknown) => typeof value !== "boolean";

const isSameCalendarMonth = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

export const getEndOfMonthDateTime = (value: unknown): Date | undefined => {
  const date = toMinute(value);
  if (!date) {
    return undefined;
  }

  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};

export const getFottServiceRequestFormSchema = (t: TFunction) => {
  const labels = {
    serviceType: t("terminal-services.fott-service-request.fields.service-type"),
    bookingFrom: t("terminal-services.fott-service-request.fields.booking-from"),
    bookingTo: t("terminal-services.fott-service-request.fields.booking-to"),
    startingFrom: t("terminal-services.fott-service-request.fields.starting-from"),
    startTime: t("terminal-services.fott-service-request.fields.start-time"),
    usageHours: t("terminal-services.fott-service-request.fields.usage-hours"),
    matrixManifold: t("terminal-services.fott-service-request.fields.matrix-manifold"),
    onBehalfOfCompany: t("terminal-services.fott-service-request.fields.on-behalf-of-company"),
    onBehalfOfCompanyOther: t(
      "terminal-services.fott-service-request.fields.on-behalf-of-company-other",
    ),
    chargeableParty: t("terminal-services.fott-service-request.fields.chargeable-party"),
    chargeablePartyOther: t("terminal-services.fott-service-request.fields.chargeable-party-other"),
    operatorNeeded: t("terminal-services.fott-service-request.fields.operator-needed"),
    customerJlSlotSize: t("terminal-services.fott-service-request.fields.customer-jl-slot-size"),
    customerJlNo: t("terminal-services.fott-service-request.fields.customer-jl-no-size"),
    customerTankNo: t("terminal-services.fott-service-request.fields.customer-tank-no"),
    fottDockLineNoSize: t("terminal-services.fott-service-request.fields.fott-dock-line-no-size"),
    fottDockLine: t("terminal-services.fott-service-request.fields.fott-dock-line-header-no-size"),
    productType: t("terminal-services.fott-service-request.fields.product-type"),
    usageReason: t("terminal-services.fott-service-request.fields.usage-reason"),
    remarks: t("terminal-services.fott-service-request.fields.remarks"),
    dateTime: t("terminal-services.fott-service-request.fields.date-time"),
    location: t("terminal-services.fott-service-request.fields.location"),
    vesselImo: t("terminal-services.fott-service-request.fields.vessel-imo"),
    vesselName: t("terminal-services.fott-service-request.fields.vessel-name"),
    terminal: t("terminal-services.fott-service-request.fields.terminal"),
    operationType: t("terminal-services.fott-service-request.fields.operation-type"),
    dockLine: t("terminal-services.fott-service-request.fields.dock-line"),
    movNumber: t("terminal-services.fott-service-request.fields.mov-number"),
    plta: t("terminal-services.fott-service-request.fields.plta"),
    pldaNumber: t("terminal-services.fott-service-request.fields.plda-number"),
    reasonForBypass: t("terminal-services.fott-service-request.fields.reason-for-bypass"),
    expectedTimeOfClosure: t(
      "terminal-services.fott-service-request.fields.expected-time-of-closure",
    ),
    physicalVerificationOfValveTag: t(
      "terminal-services.fott-service-request.fields.physical-verification-of-valve-tag",
    ),
    collectionOfValveKey: t(
      "terminal-services.fott-service-request.fields.collection-of-valve-key",
    ),
    removalOfPadlock: t("terminal-services.fott-service-request.fields.removal-of-padlock"),
    crossCheckCommunication: t(
      "terminal-services.fott-service-request.fields.cross-check-communication",
    ),
    confirmationOfInstructionsCustomer: t(
      "terminal-services.fott-service-request.fields.confirmation-of-instructions-customer",
    ),
    confirmationOfInstructionsFott: t(
      "terminal-services.fott-service-request.fields.confirmation-of-instructions-fott",
    ),
    documentType: t("terminal-services.fott-service-request.fields.document-type"),
    otherSpecify: t("terminal-services.fott-service-request.fields.other-specify"),
    issueDate: t("terminal-services.fott-service-request.fields.issue-date"),
    expirationDate: t("terminal-services.fott-service-request.fields.expiration-date"),
    documentTitle: t("terminal-services.fott-service-request.fields.document-title"),
    documentSummary: t("terminal-services.fott-service-request.fields.document-summary"),
    documentName: t("terminal-services.fott-service-request.fields.document-name"),
  };

  const fileAttachmentSchema = z.custom<File>(
    (value) => typeof File !== "undefined" && value instanceof File,
  );

  const bypassAttachmentSchema = z
    .object({
      documentName: z.string().trim(),
      documentUrl: z.string().trim(),
      documentType: z.union([z.literal(""), z.enum(TERMINAL_SERVICE_ATTACHMENT_DOCUMENT_TYPES)]),
      otherDocumentType: z
        .string()
        .trim()
        .max(30, t("form-validation.max-length", { label: labels.otherSpecify, max: 30 }))
        .optional(),
      documentTitle: z
        .string()
        .trim()
        .max(50, t("form-validation.max-length", { label: labels.documentTitle, max: 50 }))
        .optional(),
      documentSummary: z
        .string()
        .trim()
        .max(300, t("form-validation.max-length", { label: labels.documentSummary, max: 300 }))
        .optional(),
      issueDate: dateLikeSchema,
      expirationDate: dateLikeSchema,
    })
    .superRefine((data, ctx) => {
      if (!isBlank(data.documentUrl)) {
        const documentType = data.documentType;
        const issueDate = toMinute(data.issueDate);
        const expirationDate = toMinute(data.expirationDate);

        if (issueDate && issueDate.getTime() > toMinute(new Date())!.getTime()) {
          addIssue(
            ctx,
            "issueDate",
            t("form-validation.must-not-be-in-future", { label: labels.issueDate }),
          );
        }

        if (expirationDate && expirationDate.getTime() < toMinute(new Date())!.getTime()) {
          addIssue(
            ctx,
            "expirationDate",
            t("form-validation.must-not-be-in-past", { label: labels.expirationDate }),
          );
        }
        if (isBlank(data.documentName)) {
          addRequiredIssue(ctx, "documentName", labels.documentName, t);
        }

        if (isBlank(documentType)) {
          addRequiredIssue(ctx, "documentType", labels.documentType, t);
        }

        if (documentType === "OTHERS" && isBlank(data.otherDocumentType)) {
          addRequiredIssue(ctx, "otherDocumentType", labels.otherSpecify, t);
        }

        if (documentType === "CERTIFICATE" && !toMinute(data.expirationDate)) {
          addRequiredIssue(ctx, "expirationDate", labels.expirationDate, t);
        }
      }
    });

  return z
    .object({
      [F.SERVICE_TYPE]: z.string().trim(),
      [F.BOOKING_FROM]: dateLikeSchema,
      [F.BOOKING_TO]: dateLikeSchema,
      [F.STARTING_FROM]: dateLikeSchema,
      [F.START_TIME]: dateLikeSchema,
      [F.USAGE_HOURS]: z.string().trim(),
      [F.MATRIX_MANIFOLD]: z.string().trim(),
      [F.ON_BEHALF_OF_COMPANY]: z.string().trim(),
      [F.ON_BEHALF_OF_COMPANY_OTHER]: z
        .string()
        .trim()
        .max(
          MAX_GANTRY_CRANE_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.onBehalfOfCompanyOther,
            max: MAX_GANTRY_CRANE_TEXT_LENGTH,
          }),
        ),
      [F.CHARGEABLE_PARTY]: z.string().trim(),
      [F.CHARGEABLE_PARTY_OTHER]: z
        .string()
        .trim()
        .max(
          MAX_GANTRY_CRANE_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.chargeablePartyOther,
            max: MAX_GANTRY_CRANE_TEXT_LENGTH,
          }),
        ),
      [F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]: z.boolean(),
      [F.OPERATOR_NEEDED]: z.boolean(),
      [F.CUSTOMER_JL_SLOT_SIZE]: z
        .string()
        .trim()
        .max(
          MAX_NITROGEN_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.customerJlSlotSize,
            max: MAX_NITROGEN_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.CUSTOMER_JL_NO]: z
        .string()
        .trim()
        .max(
          MAX_PIGGING_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.customerJlNo,
            max: MAX_PIGGING_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.CUSTOMER_TANK_NO]: z
        .string()
        .trim()
        .max(
          MAX_PIGGING_TANK_NO_LENGTH,
          t("form-validation.max-length", {
            label: labels.customerTankNo,
            max: MAX_PIGGING_TANK_NO_LENGTH,
          }),
        ),
      [F.FOTT_DOCK_LINE_NO_SIZE]: z
        .string()
        .trim()
        .max(
          MAX_NITROGEN_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.fottDockLineNoSize,
            max: MAX_NITROGEN_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.FOTT_DOCK_LINE]: z
        .string()
        .trim()
        .max(
          MAX_PIGGING_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.fottDockLine,
            max: MAX_PIGGING_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.PRODUCT_TYPE]: z.string().trim(),
      [F.USAGE_REASON]: z
        .string()
        .trim()
        .max(
          Math.max(MAX_NITROGEN_TEXT_LENGTH, MAX_PIGGING_TEXT_LENGTH),
          t("form-validation.max-length", {
            label: labels.usageReason,
            max: Math.max(MAX_NITROGEN_TEXT_LENGTH, MAX_PIGGING_TEXT_LENGTH),
          }),
        ),
      [F.REMARKS]: z
        .string()
        .trim()
        .max(
          MAX_FOTT_SERVICE_REMARKS_LENGTH,
          t("form-validation.max-length", {
            label: labels.remarks,
            max: MAX_FOTT_SERVICE_REMARKS_LENGTH,
          }),
        ),
      [F.OPERATOR_LICENSE]: z.array(z.any()).max(MAX_OPERATOR_LICENSE_FILES).optional(),
      [F.ATTACHMENT]: z
        .array(z.union([fileAttachmentSchema, bypassAttachmentSchema]))
        .max(Math.max(MAX_NITROGEN_ATTACHMENTS, MAX_PIGGING_ATTACHMENTS, MAX_BYPASS_ATTACHMENTS))
        .optional(),
      [F.DATE_TIME]: dateLikeSchema,
      [F.LOCATION]: z.string().trim(),
      [F.VESSEL_IMO]: z
        .string()
        .trim()
        .max(
          MAX_VESSEL_IMO_LENGTH,
          t("form-validation.max-length", {
            label: labels.vesselImo,
            max: MAX_VESSEL_IMO_LENGTH,
          }),
        ),
      [F.VESSEL_NAME]: z.string().trim(),
      [F.TERMINAL]: z.string().trim(),
      [F.OPERATION_TYPE]: z.string().trim(),
      [F.DOCK_LINE]: z
        .string()
        .trim()
        .max(
          MAX_BYPASS_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.dockLine,
            max: MAX_BYPASS_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.MOV_NUMBER]: z
        .string()
        .trim()
        .max(
          MAX_BYPASS_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.movNumber,
            max: MAX_BYPASS_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.PLTA]: z
        .string()
        .trim()
        .max(
          MAX_BYPASS_SHORT_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.plta,
            max: MAX_BYPASS_SHORT_TEXT_LENGTH,
          }),
        ),
      [F.PLDA_NUMBER]: z.string().trim(),
      [F.REASON_FOR_BYPASS]: z
        .string()
        .trim()
        .max(
          MAX_BYPASS_TEXT_LENGTH,
          t("form-validation.max-length", {
            label: labels.reasonForBypass,
            max: MAX_BYPASS_TEXT_LENGTH,
          }),
        ),
      [F.EXPECTED_TIME_OF_CLOSURE]: dateLikeSchema,
      [F.PHYSICAL_VERIFICATION_OF_VALVE_TAG]: z.boolean().nullable(),
      [F.COLLECTION_OF_VALVE_KEY]: z.boolean().nullable(),
      [F.REMOVAL_OF_PADLOCK]: z.boolean().nullable(),
      [F.CROSS_CHECK_COMMUNICATION]: z.boolean().nullable(),
      [F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER]: z.boolean().nullable(),
      [F.CONFIRMATION_OF_INSTRUCTIONS_FOTT]: z.boolean().nullable(),
    })
    .superRefine((data, ctx) => {
      if (isBlank(data[F.SERVICE_TYPE])) {
        addRequiredIssue(ctx, F.SERVICE_TYPE, labels.serviceType, t);
        return;
      }

      const validateCompanyFields = () => {
        if (isBlank(data[F.ON_BEHALF_OF_COMPANY])) {
          addRequiredIssue(ctx, F.ON_BEHALF_OF_COMPANY, labels.onBehalfOfCompany, t);
        } else if (
          data[F.ON_BEHALF_OF_COMPANY] === OTHER_COMPANY_VALUE &&
          isBlank(data[F.ON_BEHALF_OF_COMPANY_OTHER])
        ) {
          addRequiredIssue(ctx, F.ON_BEHALF_OF_COMPANY_OTHER, labels.onBehalfOfCompanyOther, t);
        }

        if (!data[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF] && isBlank(data[F.CHARGEABLE_PARTY])) {
          addRequiredIssue(ctx, F.CHARGEABLE_PARTY, labels.chargeableParty, t);
        } else if (
          data[F.CHARGEABLE_PARTY] === OTHER_COMPANY_VALUE &&
          !data[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF] &&
          isBlank(data[F.CHARGEABLE_PARTY_OTHER])
        ) {
          addRequiredIssue(ctx, F.CHARGEABLE_PARTY_OTHER, labels.chargeablePartyOther, t);
        }
      };

      if (data[F.SERVICE_TYPE] === FottServiceType.PIGGING_SERVICE) {
        const startingFrom = toMinute(data[F.STARTING_FROM]);

        if (!startingFrom) {
          addRequiredIssue(ctx, F.STARTING_FROM, labels.startingFrom, t);
        } else if (startingFrom.getTime() < toMinute(new Date())!.getTime()) {
          addIssue(
            ctx,
            F.STARTING_FROM,
            t("form-validation.must-not-be-in-past", { label: labels.startingFrom }),
          );
        }

        validateCompanyFields();

        if (isBlank(data[F.CUSTOMER_JL_NO])) {
          addRequiredIssue(ctx, F.CUSTOMER_JL_NO, labels.customerJlNo, t);
        }

        if (isBlank(data[F.CUSTOMER_TANK_NO])) {
          addRequiredIssue(ctx, F.CUSTOMER_TANK_NO, labels.customerTankNo, t);
        }

        if (isBlank(data[F.FOTT_DOCK_LINE])) {
          addRequiredIssue(ctx, F.FOTT_DOCK_LINE, labels.fottDockLine, t);
        }

        if (isBlank(data[F.PRODUCT_TYPE])) {
          addRequiredIssue(ctx, F.PRODUCT_TYPE, labels.productType, t);
        } else if (
          !Object.values(PiggingProductType).includes(data[F.PRODUCT_TYPE] as PiggingProductType)
        ) {
          addIssue(
            ctx,
            F.PRODUCT_TYPE,
            t("form-validation.invalid-option", { label: labels.productType }),
          );
        }

        return;
      }

      if (data[F.SERVICE_TYPE] === FottServiceType.NITROGEN_SUPPLY_SERVICE) {
        const startTime = toMinute(data[F.START_TIME]);
        const usageHours = data[F.USAGE_HOURS]?.trim();

        if (!startTime) {
          addRequiredIssue(ctx, F.START_TIME, labels.startTime, t);
        } else if (startTime.getTime() < toMinute(new Date())!.getTime()) {
          addIssue(
            ctx,
            F.START_TIME,
            t("form-validation.must-not-be-in-past", { label: labels.startTime }),
          );
        }

        if (
          usageHours &&
          !new RegExp(`^\\d{1,${MAX_NITROGEN_USAGE_HOURS_DIGITS}}$`).test(usageHours)
        ) {
          addIssue(
            ctx,
            F.USAGE_HOURS,
            t("form-validation.max-length-digits", {
              label: labels.usageHours,
              maxDigits: MAX_NITROGEN_USAGE_HOURS_DIGITS,
            }),
          );
        }

        validateCompanyFields();

        if (isBlank(data[F.CUSTOMER_JL_SLOT_SIZE])) {
          addRequiredIssue(ctx, F.CUSTOMER_JL_SLOT_SIZE, labels.customerJlSlotSize, t);
        }

        if (isBlank(data[F.FOTT_DOCK_LINE_NO_SIZE])) {
          addRequiredIssue(ctx, F.FOTT_DOCK_LINE_NO_SIZE, labels.fottDockLineNoSize, t);
        }

        return;
      }

      if (data[F.SERVICE_TYPE] === FottServiceType.BYPASS_SERVICE) {
        const dateTime = toMinute(data[F.DATE_TIME]);
        const expectedTimeOfClosure = toMinute(data[F.EXPECTED_TIME_OF_CLOSURE]);
        const pldaNumber = data[F.PLDA_NUMBER];
        const pldaNumberFormat = new RegExp("^[0-9]{4}-[0-9]{6}$");

        if (!dateTime) {
          addRequiredIssue(ctx, F.DATE_TIME, labels.dateTime, t);
        } else if (dateTime.getTime() < toMinute(new Date())!.getTime()) {
          addIssue(
            ctx,
            F.DATE_TIME,
            t("form-validation.must-not-be-in-past", { label: labels.dateTime }),
          );
        }

        if (!expectedTimeOfClosure) {
          addRequiredIssue(ctx, F.EXPECTED_TIME_OF_CLOSURE, labels.expectedTimeOfClosure, t);
        }

        if (dateTime && expectedTimeOfClosure && dateTime >= expectedTimeOfClosure) {
          addIssue(
            ctx,
            F.EXPECTED_TIME_OF_CLOSURE,
            t("form-validation.must-be-after", {
              subject: labels.expectedTimeOfClosure,
              object: labels.dateTime,
            }),
          );
        }

        if (isBlank(data[F.LOCATION])) {
          addRequiredIssue(ctx, F.LOCATION, labels.location, t);
        }

        if (isBlank(data[F.TERMINAL])) {
          addRequiredIssue(ctx, F.TERMINAL, labels.terminal, t);
        }

        if (isBlank(data[F.OPERATION_TYPE])) {
          addRequiredIssue(ctx, F.OPERATION_TYPE, labels.operationType, t);
        }

        if (isBlank(data[F.DOCK_LINE])) {
          addRequiredIssue(ctx, F.DOCK_LINE, labels.dockLine, t);
        }

        if (isBlank(data[F.MOV_NUMBER])) {
          addRequiredIssue(ctx, F.MOV_NUMBER, labels.movNumber, t);
        }

        if (!isBlank(pldaNumber) && !pldaNumberFormat.test(pldaNumber)) {
          addIssue(
            ctx,
            F.PLDA_NUMBER,
            t("form-validation.invalid-format", {
              label: labels.pldaNumber,
              format: "XXXX-XXXXXX",
            }),
          );
        }

        if (isBlank(data[F.REASON_FOR_BYPASS])) {
          addRequiredIssue(ctx, F.REASON_FOR_BYPASS, labels.reasonForBypass, t);
        }

        if (isUnselectedBoolean(data[F.PHYSICAL_VERIFICATION_OF_VALVE_TAG])) {
          addRequiredIssue(
            ctx,
            F.PHYSICAL_VERIFICATION_OF_VALVE_TAG,
            labels.physicalVerificationOfValveTag,
            t,
          );
        }

        if (isUnselectedBoolean(data[F.COLLECTION_OF_VALVE_KEY])) {
          addRequiredIssue(ctx, F.COLLECTION_OF_VALVE_KEY, labels.collectionOfValveKey, t);
        }

        if (isUnselectedBoolean(data[F.REMOVAL_OF_PADLOCK])) {
          addRequiredIssue(ctx, F.REMOVAL_OF_PADLOCK, labels.removalOfPadlock, t);
        }

        if (isUnselectedBoolean(data[F.CROSS_CHECK_COMMUNICATION])) {
          addRequiredIssue(ctx, F.CROSS_CHECK_COMMUNICATION, labels.crossCheckCommunication, t);
        }

        if (isUnselectedBoolean(data[F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER])) {
          addRequiredIssue(
            ctx,
            F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER,
            labels.confirmationOfInstructionsCustomer,
            t,
          );
        }

        if (isUnselectedBoolean(data[F.CONFIRMATION_OF_INSTRUCTIONS_FOTT])) {
          addRequiredIssue(
            ctx,
            F.CONFIRMATION_OF_INSTRUCTIONS_FOTT,
            labels.confirmationOfInstructionsFott,
            t,
          );
        }
      }

      if (data[F.SERVICE_TYPE] !== FottServiceType.GANTRY_CRANE_SERVICE) {
        return;
      }

      const bookingFrom = toMinute(data[F.BOOKING_FROM]);
      const bookingTo = toMinute(data[F.BOOKING_TO]);

      if (!bookingFrom) {
        addRequiredIssue(ctx, F.BOOKING_FROM, labels.bookingFrom, t);
      } else if (bookingFrom.getTime() < toMinute(new Date())!.getTime()) {
        addIssue(
          ctx,
          F.BOOKING_FROM,
          t("form-validation.must-not-be-in-past", { label: labels.bookingFrom }),
        );
      }

      if (!bookingTo) {
        addRequiredIssue(ctx, F.BOOKING_TO, labels.bookingTo, t);
      } else if (bookingFrom && bookingTo.getTime() <= bookingFrom.getTime()) {
        addIssue(
          ctx,
          F.BOOKING_TO,
          t("form-validation.must-be-after", {
            subject: labels.bookingTo,
            object: labels.bookingFrom,
          }),
        );
      } else if (bookingFrom && !isSameCalendarMonth(bookingFrom, bookingTo)) {
        addIssue(
          ctx,
          F.BOOKING_TO,
          t("terminal-services.fott-service-request.validation.booking-to-same-month"),
        );
      }

      if (isBlank(data[F.MATRIX_MANIFOLD])) {
        addRequiredIssue(ctx, F.MATRIX_MANIFOLD, labels.matrixManifold, t);
      }

      validateCompanyFields();

      if (typeof data[F.OPERATOR_NEEDED] !== "boolean") {
        addRequiredIssue(ctx, F.OPERATOR_NEEDED, labels.operatorNeeded, t);
      }

      if (isBlank(data[F.USAGE_REASON])) {
        addRequiredIssue(ctx, F.USAGE_REASON, labels.usageReason, t);
      }
    });
};

export type FottServiceRequestFormData = z.infer<
  ReturnType<typeof getFottServiceRequestFormSchema>
>;

export const fottServiceRequestDefaultValues: FottServiceRequestFormData = {
  [F.SERVICE_TYPE]: "",
  [F.BOOKING_FROM]: null,
  [F.BOOKING_TO]: null,
  [F.STARTING_FROM]: null,
  [F.START_TIME]: null,
  [F.USAGE_HOURS]: "",
  [F.MATRIX_MANIFOLD]: "",
  [F.ON_BEHALF_OF_COMPANY]: "",
  [F.ON_BEHALF_OF_COMPANY_OTHER]: "",
  [F.CHARGEABLE_PARTY]: "",
  [F.CHARGEABLE_PARTY_OTHER]: "",
  [F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]: true,
  [F.OPERATOR_NEEDED]: true,
  [F.CUSTOMER_JL_SLOT_SIZE]: "",
  [F.CUSTOMER_JL_NO]: "",
  [F.CUSTOMER_TANK_NO]: "",
  [F.FOTT_DOCK_LINE_NO_SIZE]: "",
  [F.FOTT_DOCK_LINE]: "",
  [F.PRODUCT_TYPE]: "",
  [F.USAGE_REASON]: "",
  [F.REMARKS]: "",
  [F.OPERATOR_LICENSE]: [],
  [F.DATE_TIME]: null,
  [F.LOCATION]: "",
  [F.VESSEL_IMO]: "",
  [F.VESSEL_NAME]: "",
  [F.TERMINAL]: "",
  [F.OPERATION_TYPE]: "",
  [F.DOCK_LINE]: "",
  [F.MOV_NUMBER]: "",
  [F.PLTA]: "",
  [F.PLDA_NUMBER]: "",
  [F.REASON_FOR_BYPASS]: "",
  [F.EXPECTED_TIME_OF_CLOSURE]: null,
  [F.PHYSICAL_VERIFICATION_OF_VALVE_TAG]: null,
  [F.COLLECTION_OF_VALVE_KEY]: null,
  [F.REMOVAL_OF_PADLOCK]: null,
  [F.CROSS_CHECK_COMMUNICATION]: null,
  [F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER]: null,
  [F.CONFIRMATION_OF_INSTRUCTIONS_FOTT]: null,
  [F.ATTACHMENT]: [],
};
